import { makeStyles, shorthands } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";

/**
 * Ergänzende Optik der Transport-Vertragsmaske.
 *
 * Die Grundformen — Karten, Eingabefelder, Kachelwahl, Pillen — kommen
 * unverändert aus `immobilieContractStep/sharedStyles`, damit Transport und
 * Immobilien dieselbe Maske sind und nicht zwei ähnliche. Hier stehen nur die
 * Formen, die es dort nicht gibt: die Feldkarte mit Herkunfts-Abzeichen, die
 * Untertabelle und die Karten der Vertragsübersicht.
 */
export const useTransportStyles = makeStyles({
  /** Eine Feldkarte: Kopf mit Bezeichnung und Abzeichen, Eingabe, Hinweis. */
  fieldCard: {
    backgroundColor: BRAND.white,
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("10px"),
    paddingTop: "16px",
    paddingBottom: "16px",
    paddingLeft: "18px",
    paddingRight: "18px",
    display: "flex",
    flexDirection: "column",
    rowGap: "10px",
  },
  /** Untertabellen und Mehrfachauswahlen brauchen die volle Breite. */
  fieldCardWide: {
    gridColumnStart: "1",
    gridColumnEnd: "-1",
  },
  fieldTop: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    columnGap: "12px",
    rowGap: "6px",
    flexWrap: "wrap",
  },
  fieldLabel: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.98rem",
    fontWeight: 700,
    color: BRAND.ink,
    lineHeight: 1.25,
  },
  /** Spalte B der Felddefinition, wenn sie vom Anzeigebegriff abweicht. */
  fieldSub: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.8rem",
    color: BRAND.muted,
    marginTop: "2px",
  },
  fieldNote: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.8rem",
    color: BRAND.muted,
    lineHeight: 1.4,
  },
  /** Herkunft des Wertes: aus dem Vertrag erfasst … */
  badgeVertrag: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "5px",
    flexShrink: 0,
    paddingTop: "3px",
    paddingBottom: "3px",
    paddingLeft: "9px",
    paddingRight: "9px",
    fontSize: "0.68rem",
    fontWeight: 700,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    backgroundColor: "#eef3fa",
    color: "#2f5c8f",
    ...shorthands.border("1px", "solid", "#cfdcec"),
    ...shorthands.borderRadius("999px"),
  },
  /** … oder aus der Analyse übernommen. */
  badgeAnalyse: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "5px",
    flexShrink: 0,
    paddingTop: "3px",
    paddingBottom: "3px",
    paddingLeft: "9px",
    paddingRight: "9px",
    fontSize: "0.68rem",
    fontWeight: 700,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    backgroundColor: "#f4f1f5",
    color: "#6b5f70",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("999px"),
  },
  /** Nr. aus der Felddefinition — hilft beim Abgleich mit der Excel. */
  fieldId: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.68rem",
    fontWeight: 600,
    letterSpacing: "0.06em",
    color: "#a89fac",
  },
  /** Anzeigefeld aus der Analyse: kein Eingabefeld, sondern ein Wertfeld. */
  readout: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.95rem",
    color: BRAND.ink,
    backgroundColor: "#faf8fb",
    paddingTop: "9px",
    paddingBottom: "9px",
    paddingLeft: "12px",
    paddingRight: "12px",
    ...shorthands.borderRadius("6px"),
    ...shorthands.border("1px", "dashed", "#d9d2de"),
  },
  readoutLeer: {
    color: BRAND.muted,
    fontStyle: "italic",
  },
  /** Raster der Feldkarten innerhalb eines Blocks. */
  fieldsGrid: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "14px",
    "@media (min-width: 900px)": {
      gridTemplateColumns: "1fr 1fr",
    },
  },
  // ── Untertabelle ────────────────────────────────────────────────────────
  tableWrap: {
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("8px"),
    overflowX: "auto",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.88rem",
  },
  th: {
    textAlign: "left",
    fontSize: "0.7rem",
    fontWeight: 700,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    color: "#5d5664",
    backgroundColor: "#f7f5f8",
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "10px",
    paddingRight: "10px",
    whiteSpace: "nowrap",
    ...shorthands.borderBottom("1px", "solid", BRAND.borderDashed),
  },
  td: {
    paddingTop: "6px",
    paddingBottom: "6px",
    paddingLeft: "10px",
    paddingRight: "10px",
    verticalAlign: "middle",
    ...shorthands.borderBottom("1px", "solid", "#f0ecf2"),
  },
  tdRow: {
    fontWeight: 600,
    color: BRAND.ink,
    minWidth: "180px",
  },
  cellInput: {
    width: "100%",
    boxSizing: "border-box",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.88rem",
    color: BRAND.ink,
    backgroundColor: BRAND.white,
    paddingTop: "6px",
    paddingBottom: "6px",
    paddingLeft: "9px",
    paddingRight: "9px",
    ...shorthands.borderRadius("5px"),
    ...shorthands.border("1px", "solid", "#ded8e2"),
    outlineWidth: 0,
    ":focus": {
      ...shorthands.borderColor(BRAND.purple),
      boxShadow: `0 0 0 2px ${BRAND.purple}26`,
    },
  },
  tableActions: {
    display: "flex",
    alignItems: "center",
    columnGap: "8px",
    paddingTop: "8px",
  },
  rowDeleteBtn: {
    appearance: "none",
    cursor: "pointer",
    backgroundColor: "transparent",
    backgroundImage: "none",
    ...shorthands.border("0"),
    color: BRAND.muted,
    fontSize: "1rem",
    lineHeight: 1,
    paddingTop: "4px",
    paddingBottom: "4px",
    paddingLeft: "6px",
    paddingRight: "6px",
    ...shorthands.borderRadius("4px"),
    ":hover": {
      color: "#a32020",
      backgroundColor: "#fbeaea",
    },
  },
  // ── Vertragsübersicht ───────────────────────────────────────────────────
  overviewGrid: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "14px",
    "@media (min-width: 760px)": {
      gridTemplateColumns: "1fr 1fr",
    },
  },
  overviewCard: {
    appearance: "none",
    cursor: "pointer",
    textAlign: "left",
    backgroundColor: BRAND.white,
    backgroundImage: "none",
    fontFamily: BRAND.fontPrimary,
    display: "flex",
    alignItems: "flex-start",
    columnGap: "14px",
    paddingTop: "16px",
    paddingBottom: "16px",
    paddingLeft: "18px",
    paddingRight: "18px",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("12px"),
    transitionProperty: "border-color, box-shadow, transform",
    transitionDuration: "140ms",
    ":hover": {
      ...shorthands.borderColor(BRAND.purple),
      boxShadow: "0 6px 18px rgba(77,32,81,0.12)",
    },
  },
  overviewIcon: {
    display: "grid",
    placeItems: "center",
    width: "42px",
    height: "42px",
    flexShrink: 0,
    ...shorthands.borderRadius("10px"),
    color: BRAND.white,
  },
  overviewMain: {
    flexGrow: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    rowGap: "8px",
  },
  overviewTitle: {
    fontSize: "1rem",
    fontWeight: 700,
    color: BRAND.ink,
  },
  overviewStatus: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    fontSize: "0.78rem",
    color: BRAND.muted,
  },
  overviewDot: {
    width: "8px",
    height: "8px",
    ...shorthands.borderRadius("999px"),
    backgroundColor: "#cfc8d3",
  },
  overviewDotAktiv: {
    backgroundColor: "#3d7a4d",
  },
  overviewKpis: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: "8px",
    "@media (min-width: 520px)": {
      gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    },
  },
  overviewKpi: {
    display: "flex",
    flexDirection: "column",
    rowGap: "2px",
    minWidth: 0,
  },
  overviewKpiLabel: {
    fontSize: "0.68rem",
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: BRAND.muted,
  },
  overviewKpiValue: {
    fontSize: "0.9rem",
    fontWeight: 700,
    color: BRAND.ink,
    overflowX: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  overviewChevron: {
    flexShrink: 0,
    alignSelf: "center",
    color: BRAND.muted,
    fontSize: "1.3rem",
  },
  /** Zähler „x von y ausgefüllt" je Block. */
  blockMeta: {
    display: "flex",
    alignItems: "center",
    columnGap: "8px",
    flexWrap: "wrap",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.8rem",
    color: BRAND.muted,
  },
});
